import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { ConfigProvider, ThemeConfig } from 'antd';
import { TbCircleXFilled, TbX } from 'react-icons/tb';

import { ThemeContext } from '@/contexts/01-Unauthenticated/theme';
import { resolveThemeSelection, useThemeConfig } from '@/hooks/useThemeConfig';
import type { AppTheme, ThemeName, WorkspaceThemeConfiguration } from '@/hooks/useThemeConfig';
import { THEME_STORAGE_KEY } from '@/shared/constants';
import {
    applyPlatformComponentTokens,
    platformDarkAlgorithm,
    platformLightAlgorithm,
    platformLocale,
    withPlatformFont,
} from '@/theme';

interface ThemeProviderProps {
    children: ReactNode;
}

function tokensToCSSVars(tokens?: Record<string, unknown>) {
    if (!tokens) {
        return {};
    }

    const cssVars: Record<string, string> = {};

    Object.entries(tokens).forEach(([k, v]) => {
        // convert camelCase token names to kebab: colorPrimary -> color-primary
        const varName = `--antd-token-${k.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
        cssVars[varName] = String(v);
    });

    return cssVars;
}

const { Provider } = ThemeContext;

const clearIcon = (
    <TbCircleXFilled aria-hidden='true' focusable='false' size='1rem' style={{ verticalAlign: '-0.25rem' }} />
);

const closeIcon = <TbX aria-hidden='true' focusable='false' size='1rem' style={{ verticalAlign: '-0.2rem' }} />;

const ThemeProvider: FC<ThemeProviderProps> = ({ children }): JSX.Element => {
    const { builtInThemes } = useThemeConfig();

    const [themesMap, setThemesMap] = useState<Record<string, AppTheme>>(() => {
        const map: Record<string, AppTheme> = {};

        builtInThemes.forEach((t) => (map[t.name] = t));

        return map;
    });

    const [themeName, setThemeNameState] = useState<ThemeName>(() => {
        const saved = localStorage.getItem(THEME_STORAGE_KEY);

        return (saved as ThemeName) || builtInThemes[0].name;
    });

    const [workspaceThemeConfiguration, setWorkspaceThemeConfigurationState] = useState<
        WorkspaceThemeConfiguration | undefined
    >();

    const [resolvedThemeName, setResolvedThemeName] = useState<ThemeName>(() => {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

        return resolveThemeSelection(themeName, prefersDark);
    });

    // Handle system color-scheme changes for both the platform system theme and
    // workspace themes that define separate light and dark palettes.
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => {
            setResolvedThemeName(resolveThemeSelection(themeName, mq.matches, workspaceThemeConfiguration));
        };

        handler();

        if (themeName !== 'system' && !workspaceThemeConfiguration?.supportsColorSchemes) {
            return;
        }

        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [themeName, workspaceThemeConfiguration]);

    // If another tab changes theme, sync it
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === THEME_STORAGE_KEY && typeof e.newValue === 'string' && e.newValue !== themeName) {
                setThemeNameState(e.newValue as ThemeName);
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [themeName]);

    const registerTheme = useCallback((t: AppTheme) => {
        setThemesMap((prev) => ({ ...prev, [t.name]: t }));
    }, []);

    const setWorkspaceThemeConfiguration = useCallback((configuration?: WorkspaceThemeConfiguration) => {
        setWorkspaceThemeConfigurationState(configuration);
    }, []);

    const setTheme = useCallback((name: ThemeName) => {
        setThemeNameState(name);
        try {
            localStorage.setItem(THEME_STORAGE_KEY, name);
        } catch {}
    }, []);

    const activeTheme = themesMap[resolvedThemeName] ?? Object.values(themesMap)[0];
    const activeTokens = useMemo(() => withPlatformFont(activeTheme.tokens ?? {}), [activeTheme.tokens]);

    // map tokens to CSS custom properties (fast path for custom components)
    useEffect(() => {
        const cssVars = tokensToCSSVars(activeTokens);
        const root = document.documentElement;
        Object.entries(cssVars).forEach(([k, v]) => root.style.setProperty(k, v));
        root.setAttribute('data-app-theme', activeTheme.name);
        document.body.style.backgroundColor = activeTokens.colorBgContainer ?? '';
        document
            .querySelector<HTMLElement>('#root')
            ?.style.setProperty('background-color', activeTokens.colorBgContainer ?? '');
    }, [activeTheme.name, activeTokens]);

    // Build antd ConfigProvider theme prop
    const antdThemeConfig = useMemo(() => {
        const tokens = activeTokens;

        return {
            cssVar: true,
            algorithm:
                activeTheme.algorithm ??
                (String(resolvedThemeName).includes('dark') ? platformDarkAlgorithm : platformLightAlgorithm),
            token: tokens,
            components: {
                ...applyPlatformComponentTokens(activeTheme.components),
                Form: {
                    ...(activeTheme.components?.Form ?? {}),
                    itemMarginBottom: tokens.marginSM,
                    labelColor: tokens.colorTextTertiary,
                    labelFontSize: tokens.fontSizeSM,
                    verticalLabelPadding: `0 0 ${tokens.paddingXXS ?? 4}px`,
                },
            },
        } as ThemeConfig;
    }, [activeTheme.algorithm, activeTheme.components, activeTokens, resolvedThemeName]);

    const ctxVal = useMemo(
        () => ({
            themeName,
            resolvedThemeName,
            setTheme,
            themes: themesMap,
            registerTheme,
            setWorkspaceThemeConfiguration,
            workspaceThemeConfiguration,
        }),
        [
            themeName,
            resolvedThemeName,
            setTheme,
            themesMap,
            registerTheme,
            setWorkspaceThemeConfiguration,
            workspaceThemeConfiguration,
        ],
    );

    return (
        <Provider value={ctxVal}>
            <ConfigProvider
                locale={platformLocale}
                theme={antdThemeConfig}
                wave={{ disabled: true }}
                // Component Default
                alert={{ closeIcon }}
                drawer={{ closeIcon }}
                floatButtonGroup={{ closeIcon }}
                image={{ preview: { closeIcon } }}
                input={{ allowClear: { clearIcon } }}
                form={{
                    colon: false,
                    requiredMark: false,
                    scrollToFirstError: { behavior: 'smooth', block: 'center' },
                    validateMessages: {
                        required: 'This field is required.',
                        types: {
                            email: 'Enter a valid email address.',
                            url: 'Enter a valid URL.',
                        },
                    },
                }}
                modal={{
                    closeIcon,
                    styles: {
                        body: {
                            marginTop: '1rem',
                            marginBottom: '0.75rem',
                            paddingTop: '0.5rem',
                            paddingBottom: '1rem',
                            borderTop: `1px solid ${activeTheme.tokens?.colorBorder}`,
                            borderBottom: `1px solid ${activeTheme.tokens?.colorBorder}`,
                        },
                        content: { padding: '1rem', border: `1px solid ${activeTheme.tokens?.colorBorder}` },
                        footer: { paddingTop: '0.25rem' },
                    },
                }}
                notification={{ closeIcon }}
                textArea={{ allowClear: { clearIcon } }}
                tour={{ closeIcon }}
                menu={{
                    style: {
                        padding: '0.25rem 0',
                        border: `1px solid ${activeTheme.tokens?.colorBorder}`,
                        backgroundColor: activeTheme.tokens?.colorBgContainer,
                    },
                }}
                popover={{
                    styles: {
                        body: {
                            padding: 0,
                            border: `1px solid ${activeTheme.tokens?.colorBorder}`,
                            borderRadius: activeTheme.tokens?.borderRadius,
                        },
                    },
                }}
                popconfirm={{ styles: { body: { padding: '1rem' } } }}
                card={{
                    style: { boxShadow: 'none', borderColor: activeTheme.tokens?.colorBorder },
                }}
                avatar={{
                    style: {
                        verticalAlign: 'middle',
                        backgroundColor: activeTheme.tokens?.colorBgBase,
                        color: activeTheme.tokens?.colorText,
                    },
                }}
                list={{ style: { borderColor: activeTheme.tokens?.colorBorder } }}
                segmented={{
                    style: {
                        boxShadow: 'none',
                    },
                }}
                tabs={{ removeIcon: closeIcon, style: { borderColor: activeTheme.tokens?.colorBorder } }}
                typography={{ style: { marginTop: 0 } }}
                datePicker={{
                    styles: {
                        popup: {
                            root: {
                                border: `1px solid ${activeTheme.tokens?.colorBorder}`,
                                borderRadius: activeTheme.tokens?.borderRadius,
                                paddingTop: 0,
                                paddingBottom: 0,
                            },
                        },
                    },
                }}
                // tag={{ style: { padding: '1rem' } }}
            >
                {children}
            </ConfigProvider>
        </Provider>
    );
};

export default ThemeProvider;
